import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as Files } from '../assets/Icons/navigation/File.svg';
import { ReactComponent as Datasets } from '../assets/Icons/navigation/Dataset.svg';
import { ReactComponent as Models } from '../assets/Icons/navigation/Model.svg';
import { ReactComponent as Predictions } from '../assets/Icons/navigation/Prediction.svg';
import { ReactComponent as ReadAcross } from '../assets/Icons/navigation/Read-Across.svg';
import { ReactComponent as Jobs } from '../assets/Icons/navigation/Clock.svg';
import { ReactComponent as Users } from '../assets/Icons/navigation/User-Directory.svg';
import { ReactComponent as Help } from '../assets/Icons/navigation/Help.svg';
import { ReactComponent as OpenIcon } from '../assets/Icons/chevron/Double-Chevron-Right.svg';
import { ReactComponent as CloseIcon } from '../assets/Icons/chevron/Double-Chevron-Left.svg';


import { IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { Theme, CSSObject, styled, useTheme } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import { useSelector } from 'react-redux';
import { selectNav } from '../store/selectors/nav.selector';
import { useDispatch } from 'react-redux';
import { toggleNav } from '../store/slices/nav.slice';
import ProfileDropdown from './Menus&Dropdowns/ProfileDropdown';
import { capitalize } from '../utils/formattingFns';
import useMessage from '../hooks/useMessage';
import SnackBar from './Alerts/SnackBar';
import { useCurrentUser } from "../providers/CurrentUserProvider";
import { useAccess } from "../access/useAccess";
import { demo_access, readacross_access } from "../access/predefinedConditions";
import useColorPalette from '../hooks/useColorPalette';

const drawerWidth = 260;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  backgroundColor: '#E3E6F4',
  border: 'none',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  backgroundColor: '#E3E6F4',
  border: 'none',
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
  }));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme }) => ({
      width: drawerWidth,
      flexShrink: 0,
      whiteSpace: 'nowrap',
      boxSizing: 'border-box',
      variants: [
        {
          props: ({ open }) => open,
          style: {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
          },
        },
        {
          props: ({ open }) => !open,
          style: {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
          },
        },
      ],
    }),
  );

export default function UpdatedNav() {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [ pathname, setPathname ] = useState('');
    const [ isVisible, setIsVisible ] = useState(false);
    const isNavOpen = useSelector(selectNav);
    const { user: currentUser } = useCurrentUser();
    const { canAccess } = useAccess();
    const { palette } = useColorPalette();
    const theme = useTheme();


    const {
        open,
        setOpen,
        message,
        isSuccess,
        setPopupMessage
    } = useMessage();

    const handleDrawerOpen = () => {
        dispatch(toggleNav(true));
    };

    const handleDrawerClose = () => {
        dispatch(toggleNav(false));
    };

    let topNav = [
        {
            label: 'Files',
            path: 'files',
            action: () => navigate('/files'),
            icon: <div className={`w-[24px] ${pathname.includes('files') ? 'text-primary' : 'text-disable'}`}><Files/></div>
        },
        {
            label: 'Datasets',
            path: 'datasets',
            action: () => navigate('/datasets'),
            icon: <div className={`w-[24px] ${pathname.includes('datasets') ? 'text-primary' : 'text-disable'}`}><Datasets/></div>
        },
        {
            label: 'Models',
            path: 'models',
            action: () => navigate('/models'),
            icon: <div className={`w-[22px] ${pathname.includes('models') ? 'text-primary' : 'text-disable'}`}><Models/></div>
        },
        {
            label: 'Resultsets',
            path: 'resultsets',
            action: () => navigate('/resultsets'),
            icon: <div className={`w-[24px] ${pathname.includes('resultsets') ? 'text-primary' : 'text-disable'}`}><Predictions/></div>
        },
    ];
    if (canAccess({or: readacross_access})) topNav.push(
        {
            label: 'Read-Across',
            path: 'readacross',
            action: () => navigate('/readacross'),
            icon: <div className={`w-[24px] ${pathname.includes('readacross') ? 'text-primary' : 'text-disable'}`}><ReadAcross/></div>
        },
    );
    topNav.push(
        {
            label: 'Job Queue',
            path: 'jobs',
            action: () => navigate('/jobs'),
            icon: <div className={`w-[24px] ${pathname.includes('jobs') ? 'text-primary' : 'text-disable'}`}><Jobs/></div>
        },
    );
    if (canAccess({or: demo_access})) topNav.push(
        {
            label: 'User Directory',
            path: 'users',
            action: () => navigate('/users'),
            icon: <div className='w-[24px] h-[24px] text-disable'><Users/></div>
        },
    );

    const bottomNav = [
        {
            label: 'Help Center',
            path: 'help',
            action: () => navigate('/help'),
            icon: <div className={`w-[24px] ${pathname.includes('help') ? 'text-primary' : 'text-disable'}`}><Help/></div>
        },
    ]


    useEffect(() => {
        const path = location.pathname.replace('/', '');
        setPathname(path);
        // removes navigation bar from login screen
        if (path.length && !path.includes('login')) {
            setIsVisible(true);
        } else {
            setIsVisible(false);
        }

        // resets file, dataset and model selection when navigating out of create-new-model or make-new-prediction pages
        

    },[location.pathname]);
    
    return (
        <div className={`min-h-screen flex justify-start items-start bg-darker-background`}>
            {isVisible && (
                <Drawer variant='permanent' open={isNavOpen}>
                    <DrawerHeader>
                        <div className={`flex justify-start items-center gap-2 ${isNavOpen ? 'opacity-100 w-auto' : 'opacity-0 w-0'}`}>
                            <img 
                                id='logo' 
                                src={`${process.env.PUBLIC_URL}/${palette.logo}`} 
                                className="h-[33px] ml-[4px]" 
                                alt='logo' 
                            />
                            <p className="text-[1.5rem] text-primary font-semibold text-nowrap mr-4">{process.env.REACT_APP_META_TITLE || "Assay Central"}</p>
                        </div>
                        <IconButton onClick={isNavOpen ? handleDrawerClose : handleDrawerOpen}>
                            {isNavOpen ? <div className='w-[24px]'><CloseIcon/></div> : <div className='w-[24px]'><OpenIcon/></div>}
                        </IconButton>
                    </DrawerHeader>
                    <div className='h-full flex flex-col justify-between items-stretch'>
                        <List>
                            {topNav.map((nav,i) => (
                                <ListItem key={i} disablePadding sx={{display: 'block'}}>
                                    <ListItemButton
                                        onClick={nav.action}
                                        sx={[
                                            {
                                            minHeight: 48,
                                            px: 2.5,
                                            borderLeft: nav.path && pathname.includes(nav.path) ? '2px solid #006D77' : 'none',
                                            marginLeft: nav.path && pathname.includes(nav.path) ? '-2px' : '0',
                                            backgroundColor: nav.path && pathname.includes(nav.path) ? 'rgba(203, 206, 220, 0.5)' : 'inherit',
                                            },
                                            isNavOpen
                                            ? {
                                                justifyContent: 'initial',
                                                }
                                            : {
                                                justifyContent: 'center',
                                                },
                                        ]}
                                    >
                                        <ListItemIcon
                                            sx={[
                                                {
                                                minWidth: 0,
                                                justifyContent: 'center',
                                                },
                                                isNavOpen
                                                ? {
                                                    mr: 3,
                                                    }
                                                : {
                                                    mr: 'auto',
                                                    },
                                            ]}
                                        >
                                            {nav.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={nav.label}
                                            primaryTypographyProps={{
                                                fontFamily: 'Montserrat',
                                                fontWeight: 500,
                                                color: nav.path && pathname.includes(nav.path) ? theme.palette.primary.main : '#9292AA'
                                            }}
                                            sx={[
                                                isNavOpen
                                                ? {
                                                    opacity: 1,
                                                    }
                                                : {
                                                    opacity: 0,
                                                    },
                                            ]}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                        <List>
                            {bottomNav.map((nav,i) => (
                                <ListItem key={i} disablePadding sx={{display: 'block'}}>
                                    <ListItemButton
                                        onClick={nav.action}
                                        sx={[
                                            {
                                            minHeight: 48,
                                            px: 2.5,
                                            borderLeft: nav.path && pathname.includes(nav.path) ? '2px solid #006D77' : 'none',
                                            marginLeft: nav.path && pathname.includes(nav.path) ? '-2px' : '0',
                                            backgroundColor: nav.path && pathname.includes(nav.path) ? 'rgba(203, 206, 220, 0.5)' : 'inherit',
                                            },
                                            isNavOpen
                                            ? {
                                                justifyContent: 'initial',
                                                }
                                            : {
                                                justifyContent: 'center',
                                                },
                                        ]}
                                    >
                                        <ListItemIcon
                                            sx={[
                                                {
                                                minWidth: 0,
                                                justifyContent: 'center',
                                                },
                                                isNavOpen
                                                ? {
                                                    mr: 3,
                                                    }
                                                : {
                                                    mr: 'auto',
                                                    },
                                            ]}
                                        >
                                            {nav.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={nav.label}
                                            primaryTypographyProps={{
                                                fontFamily: 'Montserrat',
                                                fontWeight: 500,
                                                color: nav.path && pathname.includes(nav.path) ? '#006D77' : '#9292AA'
                                            }}
                                            sx={[
                                                isNavOpen
                                                ? {
                                                    opacity: 1,
                                                    }
                                                : {
                                                    opacity: 0,
                                                    },
                                            ]}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                            <ProfileDropdown 
                                isNavOpen={isNavOpen}
                                name={currentUser ? `${currentUser.first_name ? currentUser.first_name : ''} ${currentUser.last_name ? currentUser.last_name : ''}` : ''}
                                email={currentUser ? currentUser.email! : ''}
                                initials={currentUser ? `${currentUser.first_name ? capitalize(Array.from(currentUser.first_name)[0]) : ''}${currentUser.last_name ? capitalize(Array.from(currentUser.last_name)[0]) : ''}` : ''}
                                setPopupMessage={setPopupMessage}
                            />
                        </List>
                    </div>
                </Drawer>
            )}
            <div className='text-primary w-full grow min-h-screen'>
            {/* <div className={`bg-darker-background text-primary ${isVisible ? 'p-10' : 'p-0'} w-full grow min-h-screen`}> */}
                <Outlet/>
            </div>

            <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>  
        </div>
    )
}